"use client";

// Noll-render-komponent. Monteras EN gång i RootLayout. Registrerar globala
// fel-listeners + wrappar console.error/warn till activity-loggen. Returnerar
// null — ingen DOM, ingen state, ingen re-render. All kostnad ligger på
// fel-vägen (sällsynt), aldrig på happy-path.

import { useEffect } from "react";
import { pushActivity } from "@/lib/activity-log";
import { reportError } from "@/lib/error-report";

export default function ErrorCapture() {
  useEffect(() => {
    // 1. Console-wrap → ring buffer (O(1) push + vidarekoppling).
    const origError = console.error;
    const origWarn = console.warn;
    function toLabel(args: unknown[]): string {
      return args
        .map((a) => (typeof a === "string" ? a : String(a)))
        .join(" ")
        .slice(0, 200);
    }
    console.error = (...args: unknown[]) => {
      try {
        pushActivity({ type: "konsol", label: `error: ${toLabel(args)}` });
      } catch {
        /* svälj */
      }
      origError.apply(console, args);
    };
    console.warn = (...args: unknown[]) => {
      try {
        pushActivity({ type: "konsol", label: `warn: ${toLabel(args)}` });
      } catch {
        /* svälj */
      }
      origWarn.apply(console, args);
    };

    // 2. Ohanterade fel utanför React-render.
    function onError(ev: ErrorEvent) {
      reportError({
        kalla: "window-onerror",
        meddelande: ev.message || "window.onerror",
        stack: ev.error?.stack,
      });
    }
    function onRejection(ev: PromiseRejectionEvent) {
      const r = ev.reason as { message?: string; stack?: string } | undefined;
      reportError({
        kalla: "unhandledrejection",
        meddelande: r?.message ?? String(ev.reason),
        stack: r?.stack,
      });
    }
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      console.error = origError;
      console.warn = origWarn;
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
